import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, CreditCard, Save, Search, Loader2 } from "lucide-react";
import { API } from "../config";

/**
 * Admin credit-account ledger.
 *
 * Lists every customer that has been granted a 30-day credit facility,
 * showing their limit, outstanding (open credit orders) and remaining
 * available balance. Lets the admin upsert / remove accounts.
 *
 * The "outstanding" figure is computed live by the backend by summing
 * unpaid credit orders for the customer's email.
 */
export default function CreditAccounts({ onToast }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    customer_email: "",
    customer_name: "",
    credit_limit: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/credit-accounts`);
      setAccounts(res.data || []);
    } catch {
      onToast?.({ message: "Failed to load credit accounts", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_email) {
      onToast?.({ message: "Customer email required", type: "error" });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/credit-accounts`, {
        customer_email: form.customer_email.trim(),
        customer_name: form.customer_name.trim(),
        credit_limit: parseFloat(form.credit_limit || 0),
        notes: form.notes,
      });
      onToast?.({ message: "Credit account saved", type: "success" });
      setForm({ customer_email: "", customer_name: "", credit_limit: "", notes: "" });
      await load();
    } catch (err) {
      onToast?.({ message: err.response?.data?.detail || "Failed to save", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove credit facility for ${email}? Open orders will still be listed but the limit will be deleted.`)) return;
    try {
      await axios.delete(`${API}/admin/credit-accounts/${encodeURIComponent(email)}`);
      onToast?.({ message: "Credit account removed", type: "success" });
      await load();
    } catch (err) {
      onToast?.({ message: err.response?.data?.detail || "Failed to remove", type: "error" });
    }
  };

  const toggleDetails = async (email) => {
    if (expanded === email) {
      setExpanded(null);
      return;
    }
    setExpanded(email);
    if (!details[email]) {
      try {
        const res = await axios.get(`${API}/admin/credit-accounts/${encodeURIComponent(email)}`);
        setDetails((prev) => ({ ...prev, [email]: res.data }));
      } catch {
        /* no-op */
      }
    }
  };

  const filtered = accounts.filter((a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (a.customer_email || "").toLowerCase().includes(q)
        || (a.customer_name || "").toLowerCase().includes(q);
  });

  const utilisationColor = (out, limit) => {
    if (!limit) return "bg-gray-300";
    const pct = out / limit;
    if (pct < 0.5) return "bg-emerald-500";
    if (pct < 0.85) return "bg-amber-400";
    return "bg-red-500";
  };

  return (
    <div className="space-y-6" data-testid="credit-accounts-tab">
      {/* Upsert form */}
      <form onSubmit={submit} className="card p-6">
        <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-[#00a99d]" /> Grant / Update Credit Facility
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Set a credit limit for a customer (matched by email). Outstanding balance
          is updated automatically as their 30-day credit orders are placed and paid.
        </p>
        <div className="grid md:grid-cols-4 gap-3">
          <div className="md:col-span-1">
            <label className="block text-sm font-medium mb-1">Customer email *</label>
            <input type="email" required className="form-input" value={form.customer_email}
              onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
              data-testid="credit-email" />
          </div>
          <div className="md:col-span-1">
            <label className="block text-sm font-medium mb-1">Customer / business name</label>
            <input type="text" className="form-input" value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              data-testid="credit-name" />
          </div>
          <div className="md:col-span-1">
            <label className="block text-sm font-medium mb-1">Credit limit (&pound;)</label>
            <input type="number" min="0" step="0.01" className="form-input" value={form.credit_limit}
              onChange={(e) => setForm({ ...form, credit_limit: e.target.value })}
              data-testid="credit-limit" />
          </div>
          <div className="md:col-span-1">
            <label className="block text-sm font-medium mb-1">Notes</label>
            <input type="text" className="form-input" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Approved by, reference, etc."
              data-testid="credit-notes" />
          </div>
        </div>
        <button type="submit" disabled={saving} className="btn-primary mt-4 inline-flex items-center gap-2"
          data-testid="credit-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save credit account"}
        </button>
      </form>

      {/* Search + list */}
      <div className="card overflow-x-auto">
        <div className="p-4 border-b flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <input type="text" placeholder="Search by name or email…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="form-input pl-9" data-testid="credit-search" />
          </div>
          <span className="text-sm text-gray-500" data-testid="credit-count">
            {filtered.length} account{filtered.length === 1 ? "" : "s"}
          </span>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-400" data-testid="credit-empty">
            No credit accounts yet. Add one above.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Email</th>
                <th className="text-right">Limit</th>
                <th className="text-right">Outstanding</th>
                <th className="text-right">Available</th>
                <th>Utilisation</th>
                <th>Open orders</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const pct = a.credit_limit > 0 ? Math.min(100, Math.round((a.outstanding / a.credit_limit) * 100)) : 0;
                const isExpanded = expanded === a.customer_email;
                const detail = details[a.customer_email];
                return (
                  <React.Fragment key={a.customer_email}>
                    <tr className="cursor-pointer hover:bg-gray-50" onClick={() => toggleDetails(a.customer_email)}>
                      <td className="font-medium">{a.customer_name || "—"}</td>
                      <td className="text-sm text-gray-600">{a.customer_email}</td>
                      <td className="text-right">&pound;{Number(a.credit_limit).toFixed(2)}</td>
                      <td className="text-right text-red-600">&pound;{Number(a.outstanding).toFixed(2)}</td>
                      <td className="text-right font-semibold text-emerald-700">&pound;{Number(a.available).toFixed(2)}</td>
                      <td>
                        <div className="w-24 h-2 bg-gray-200 rounded overflow-hidden">
                          <div className={`h-full ${utilisationColor(a.outstanding, a.credit_limit)}`}
                            style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] text-gray-500">{pct}%</span>
                      </td>
                      <td>{a.open_orders}</td>
                      <td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => {
                            setForm({
                              customer_email: a.customer_email,
                              customer_name: a.customer_name || "",
                              credit_limit: String(a.credit_limit || ""),
                              notes: a.notes || "",
                            });
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          className="text-[#00a99d] hover:underline text-sm mr-3"
                          data-testid={`credit-edit-${a.customer_email}`}>
                          Edit
                        </button>
                        <button onClick={() => remove(a.customer_email)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`credit-delete-${a.customer_email}`}>
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan="8" className="p-4">
                          <div className="text-sm">
                            <div className="font-semibold mb-2">Credit-order history</div>
                            {detail?.orders?.length ? (
                              <table className="w-full text-xs">
                                <thead className="text-gray-500">
                                  <tr>
                                    <th className="text-left">Order #</th>
                                    <th className="text-left">Date</th>
                                    <th className="text-left">Status</th>
                                    <th className="text-right">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detail.orders.map((o) => (
                                    <tr key={o.id} className="border-t border-gray-200">
                                      <td className="font-mono py-1">{o.id.slice(0, 8).toUpperCase()}</td>
                                      <td>{new Date(o.created_at).toLocaleDateString()}</td>
                                      <td>
                                        <span className={`badge status-${o.status}`}>{o.status}</span>
                                      </td>
                                      <td className="text-right">&pound;{Number(o.total).toFixed(2)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <span className="text-gray-400 italic">No credit orders yet.</span>
                            )}
                            {a.notes && <p className="mt-2 text-gray-500 italic">Notes: {a.notes}</p>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
